import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';

export default function SharedNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [overDark, setOverDark] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLang, switchLang } = useLanguage();

  const navLinks = [
    { labelKey: 'nav_glasses', href: '/NIMO', zhOnly: false },
    { labelKey: 'nav_brand', href: '#brand', zhOnly: false },
    { labelKey: 'nav_dealer', href: '/dealer', zhOnly: true },
  ].filter((link) => !link.zhOnly || currentLang === 'zh');

  const checkSection = useCallback(() => {
    const NAVBAR_H = 64;
    const sections = document.querySelectorAll('[data-dark-section]');
    let found = false;
    sections.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < NAVBAR_H && rect.bottom > 0) found = true;
    });
    setOverDark(found);
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    // 立即执行一次，确保初始状态正确
    checkSection();
    // 再延迟执行一次，等待页面 DOM 渲染完成（图片、懒加载组件等）
    const t1 = setTimeout(checkSection, 100);
    const t2 = setTimeout(checkSection, 400);
    window.addEventListener('scroll', checkSection, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkSection);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname, checkSection]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // 点击外部关闭移动端菜单
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('header')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.querySelector(href);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 350);
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isActive = (href: string) => {
    if (href.startsWith('#')) return false;
    return location.pathname === href;
  };

  const showWhite = overDark;
  const showSolidBg = scrolled && !overDark;
  const textBase = showWhite ? 'text-white' : 'text-stone-900';
  const textMuted = showWhite ? 'text-white/70 hover:text-white' : 'text-stone-500 hover:text-stone-900';
  const dividerColor = showWhite ? 'bg-white/30' : 'bg-stone-200';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-400 ${
        showSolidBg ? 'bg-white border-b border-stone-100' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">

        {/* Left spacer (desktop) */}
        <div className="hidden md:flex flex-1" />

        {/* Center: Logo + Nav */}
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => handleNav('/')} className="flex items-center gap-2.5 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/a7fd895d0fa7eb29a2dd8ac7cc9abc15.png"
              alt="NIMO AI Logo"
              className="h-8 w-auto object-contain"
              loading="eager"
              referrerPolicy="no-referrer"
            />
            <span className={`font-sans text-base font-semibold tracking-[0.2em] transition-colors duration-300 ${textBase}`}>
              NIMO
            </span>
          </button>

          <span className={`w-px h-4 transition-colors duration-300 ${dividerColor}`} />

          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className={`font-sans text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer whitespace-nowrap relative ${
                isActive(link.href) ? textBase : textMuted
              }`}
            >
              {t(link.labelKey)}
              {isActive(link.href) && (
                <span className="absolute -bottom-1 left-0 w-full h-px bg-current" />
              )}
            </button>
          ))}
        </div>

        {/* Mobile: Logo */}
        <button onClick={() => handleNav('/')} className="flex md:hidden items-center gap-2.5 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/a7fd895d0fa7eb29a2dd8ac7cc9abc15.png"
            alt="NIMO AI Logo"
            className="h-8 w-auto object-contain"
            loading="eager"
            referrerPolicy="no-referrer"
          />
          <span className={`font-sans text-base font-semibold tracking-[0.2em] transition-colors duration-300 ${textBase}`}>
            NIMO
          </span>
        </button>

        {/* Right: Lang switcher + CTA + Mobile menu */}
        <div className="flex-1 flex items-center justify-end gap-3">

          {/* Join Waitlist (EN only) — 占位保持语言切换位置稳定 */}
          <div className="hidden md:block">
            {currentLang === 'en' ? (
              <button
                onClick={() => handleNav('#preorder')}
                className={`px-3 py-1 text-xs font-sans font-medium tracking-wide rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  showWhite
                    ? 'border-white text-white hover:bg-white hover:text-stone-900'
                    : 'border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white'
                }`}
              >
                Join Waitlist
              </button>
            ) : (
              <div className="px-3 py-1 text-xs invisible whitespace-nowrap">Join Waitlist</div>
            )}
          </div>

          {/* Language switcher pill */}
          <div
            className={`hidden md:flex items-center rounded-full overflow-hidden border transition-colors duration-300 ${
              showWhite ? 'border-white/30' : 'border-stone-200'
            }`}
          >
            <button
              onClick={() => switchLang('zh')}
              className={`px-3 py-1 text-xs font-sans font-medium tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap ${
                currentLang === 'zh'
                  ? showWhite ? 'bg-white text-stone-900' : 'bg-stone-900 text-white'
                  : showWhite ? 'text-white/60 hover:text-white' : 'text-stone-400 hover:text-stone-700'
              }`}
            >
              中文
            </button>
            <button
              onClick={() => switchLang('en')}
              className={`px-3 py-1 text-xs font-sans font-medium tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap ${
                currentLang === 'en'
                  ? showWhite ? 'bg-white text-stone-900' : 'bg-stone-900 text-white'
                  : showWhite ? 'text-white/60 hover:text-white' : 'text-stone-400 hover:text-stone-700'
              }`}
            >
              EN
            </button>
          </div>

          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              showWhite ? 'text-white' : 'text-stone-800'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className={`text-left font-sans text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                isActive(link.href) ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
              }`}
            >
              {t(link.labelKey)}
            </button>
          ))}

          {/* Mobile Join Waitlist (EN only) */}
          {currentLang === 'en' && (
            <button
              onClick={() => handleNav('#preorder')}
              className="w-full text-center py-3 bg-stone-900 text-white text-sm font-sans font-medium tracking-wide rounded-full cursor-pointer whitespace-nowrap"
            >
              Join Waitlist
            </button>
          )}

          {/* Mobile language switcher */}
          <div className="flex items-center gap-3 pt-2 border-t border-stone-100">
            <span className="text-xs text-stone-400 font-sans">Language</span>
            <div className="flex items-center rounded-full overflow-hidden border border-stone-200">
              <button
                onClick={() => switchLang('zh')}
                className={`px-3 py-1 text-xs font-sans font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  currentLang === 'zh' ? 'bg-stone-900 text-white' : 'text-stone-400 hover:text-stone-700'
                }`}
              >
                中文
              </button>
              <button
                onClick={() => switchLang('en')}
                className={`px-3 py-1 text-xs font-sans font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  currentLang === 'en' ? 'bg-stone-900 text-white' : 'text-stone-400 hover:text-stone-700'
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
